import { AppBuilderStreamAPIName } from 'src/constants';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import type {
  ProductAgentQuestion,
  ProductAgentResponse,
} from './appBuilderApi';

import { ApiMethods, ApiTags, appAPI } from '.';

// Roles are LLM-canonical: user / assistant / system. The kind of
// content a message carries is encoded by its parts (see MessagePart).
// An assistant message can interleave text and tool_use parts the way
// Anthropic's Messages API does; a system message carries deploy events
// or status text; a user message is the original prompt or future
// tool_result data.
export type BuilderMessageRole = 'user' | 'assistant' | 'system' | 'narrator';

// Part type discriminator. Renderers branch on this. New content kinds
// ship as new part types — `role` does not grow.
export type MessagePartType =
  | 'text'
  | 'tool_use'
  | 'tool_result'
  | 'task_list'
  | 'error'
  | 'thinking_v1'
  | 'log'
  | 'narrator_thinking'
  | 'ack'
  | 'product_agent_questions'
  | 'product_agent_prompt';

// MessagePart mirrors backend builder_message.MessagePart. Only the
// fields documented for that part type are populated; the rest are
// undefined.
export interface MessagePart {
  type: string;
  // type=text
  text?: string;
  // type=tool_use
  toolUseId?: string;
  toolName?: string;
  // Backend sends ToolInput as `json.RawMessage`, which serializes as
  // inline JSON (object/array/primitive) rather than a JSON-encoded
  // string. Typed as `unknown` so renderers must narrow before use.
  toolInput?: unknown;
  // type=tool_result
  toolResultFor?: string;
  toolResult?: string;
  toolIsError?: boolean;
  // type=task_list — `tasks` carries the inline task array (backend
  // serializes it from `json.RawMessage`, not as a JSON-encoded string);
  // `currentIndex` points at the task currently in flight.
  tasks?: unknown[];
  currentIndex?: number;
  // type=error
  errorMessage?: string;
  // type=thinking_v1
  thinkingText?: string;
  // type=log — raw model output, not for display
  logText?: string;
  // type=narrator_thinking — terse chain-of-thought summary
  narratorText?: string;
  // type=product_agent_questions
  questions?: ProductAgentQuestion[];
  // type=product_agent_prompt
  finalizedPrompt?: string;
  approvedAt?: string;
}

export interface BuilderMessage {
  id: string;
  builderSessionId: string;
  role: string;
  parts: MessagePart[];
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Extract the concatenated text from a message's text parts. */
export const getMessageText = (msg: BuilderMessage): string =>
  (msg.parts ?? [])
    .filter((p) => p.type === 'text')
    .map((p) => p.text ?? '')
    .join('\n\n');

export interface CustomSkill {
  name: string;
  content: string;
}

export interface SkillsConfig {
  disabledBuiltins?: string[];
  customSkills?: CustomSkill[];
}

export interface BuilderSession {
  id: string;
  appBuilderId: string;
  claudeSessionId?: string;
  summary?: string;
  // Non-empty while a turn is in flight; cleared when the turn completes.
  // Drives the chat thinking indicator and composer lock.
  activeTurnId?: string;
  activeTurnMode?: 'plan';
  activeTaskListMessageId?: string;
  // Ephemeral status phrase set by the narrator Haiku; cleared when the turn ends.
  narratorStatus?: string;
  skillsConfig?: SkillsConfig;
  createdAt?: string;
  updatedAt?: string;
}

export const getBuilderSessionEntityTag = (id: string) => ({
  type: ApiTags.builderSessions as const,
  id,
});

export interface ListBuilderSessionsResponse {
  data: BuilderSession[];
  nextToken?: string;
}

export const builderSessionApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    listBuilderSessions: build.query<
      ListBuilderSessionsResponse,
      { appBuilderId: string }
    >({
      query: ({ appBuilderId }) => ({
        path: `/app-builders/${appBuilderId}/sessions`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    getBuilderSession: build.query<
      BuilderSession,
      { appBuilderId: string; sessionId: string }
    >({
      query: ({ appBuilderId, sessionId }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${sessionId}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getBuilderSessionEntityTag(arg.sessionId)],
    }),
    sendBuilderSessionMessage: build.mutation<
      { data: { turnId: string; messageId: string } },
      {
        appBuilderId: string;
        builderSessionId: string;
        content?: string;
        // Set to "plan" to dispatch a planning turn — the assistant
        // message gets a task_list part appended on done.
        mode?: 'plan';
        // Optional prompt slot selections (slug → context item ID) from the
        // build inspector. Only honored when the app_builder_inspector flag
        // is on for the caller's portal.
        promptSelections?: Record<string, string>;
        // Persisted on the session at plan-start so the backend's
        // plan-done auto-kickoff has the skill set already on the
        // session. Only honored when mode === "plan".
        skillsConfig?: SkillsConfig;
        // When true, dispatch the plan turn from the persisted
        // product_agent_prompt instead of sending fresh user content.
        // Requires mode === 'plan'. Backend resolves the prompt text
        // server-side and stamps approvedAt on the assistant part.
        approve?: boolean;
      }
    >({
      query: ({
        appBuilderId,
        builderSessionId,
        content,
        mode,
        promptSelections,
        skillsConfig,
        approve,
      }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/messages`,
        method: ApiMethods.post,
        options: {
          body: approve
            ? {
                approve: true,
                mode,
                skillsConfig,
              }
            : {
                content,
                mode,
                ...(promptSelections && Object.keys(promptSelections).length > 0
                  ? { promptSelections }
                  : {}),
                skillsConfig,
              },
        },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    createBuilderSession: build.mutation<
      BuilderSession,
      { appBuilderId: string }
    >({
      query: ({ appBuilderId }) => ({
        path: `/app-builders/${appBuilderId}/sessions`,
        method: ApiMethods.post,
        options: { body: {} },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    updateBuilderSessionSkillsConfig: build.mutation<
      BuilderSession,
      {
        appBuilderId: string;
        builderSessionId: string;
        skillsConfig: SkillsConfig;
      }
    >({
      query: ({ appBuilderId, builderSessionId, skillsConfig }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/skills-config`,
        method: ApiMethods.patch,
        options: { body: { skillsConfig } },
      }),
      invalidatesTags: (_, __, arg) => [
        getBuilderSessionEntityTag(arg.builderSessionId),
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    sendProductAgentSessionMessage: build.mutation<
      ProductAgentResponse,
      {
        appBuilderId: string;
        builderSessionId: string;
        content?: string;
        promptSelections?: Record<string, string>;
      }
    >({
      query: ({
        appBuilderId,
        builderSessionId,
        content,
        promptSelections,
      }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/product-agent`,
        method: ApiMethods.post,
        options: {
          body: {
            content: content ?? '',
            ...(promptSelections && Object.keys(promptSelections).length > 0
              ? { promptSelections }
              : {}),
          },
        },
      }),
    }),
  }),
});

export const {
  useListBuilderSessionsQuery,
  useGetBuilderSessionQuery,
  useSendBuilderSessionMessageMutation,
  useCreateBuilderSessionMutation,
  useUpdateBuilderSessionSkillsConfigMutation,
  useSendProductAgentSessionMessageMutation,
} = builderSessionApi;

export const streamProductAgentMessage = async (
  {
    appBuilderId,
    builderSessionId,
    content,
    promptSelections,
    signal,
  }: {
    appBuilderId: string;
    builderSessionId: string;
    content?: string;
    promptSelections?: Record<string, string>;
    signal?: AbortSignal;
  },
  onMessage: (msg: BuilderMessage) => void,
): Promise<void> => {
  const url = buildApiEndpointUrl(
    AppBuilderStreamAPIName,
    `/app-builders/${appBuilderId}/sessions/${builderSessionId}/product-agent/stream`,
  );
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({ content: content ?? '', promptSelections }),
    headers: { Accept: 'text/event-stream' },
    signal,
  });
  let response: Response;
  try {
    response = await fetch(url, requestInit);
  } catch (err) {
    // Caller aborted — not a real failure.
    if ((err as Error)?.name === 'AbortError') return;
    throw err;
  }
  if (!response.ok || !response.body) {
    throw new Error(`PA stream failed: ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let receivedDone = false;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) {
        if (!block.trim()) continue;
        let eventType = 'message';
        let data = '';
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) eventType = line.slice(6).trim();
          else if (line.startsWith('data:')) data = line.slice(5).trim();
        }
        if (eventType === 'message' && data) {
          try {
            const parsed = JSON.parse(data) as {
              event: string;
              item: BuilderMessage;
            };
            if (parsed.item?.id) onMessage(parsed.item);
          } catch {
            // skip malformed
          }
        }
        if (eventType === 'error')
          throw new Error('PA stream failed: server error');
        if (eventType === 'done') {
          receivedDone = true;
          return;
        }
      }
    }
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return;
    throw err;
  } finally {
    reader.releaseLock();
  }
  if (!receivedDone) {
    throw new Error('PA stream closed without a done event');
  }
};
